(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-bdk-header-2026]';
  const CONFIG_SELECTOR = '[data-bdk-header-config]';
  const PREVIEWS_SELECTOR = '[data-bdk-menu-previews]';
  const PREVIEW_HOVER_INTENT_MS = 100;
  const PREVIEW_RESET_DELAY_MS = 150;
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  const instances = new WeakMap();

  class BadinkaMobileMenu2026 {
    constructor(root) {
      this.root = root;
      this.header = root.querySelector('[data-bdk-mobile-header]');
      this.toggle = root.querySelector('[data-bdk-mobile-menu-toggle]');
      this.menu = root.querySelector('[data-bdk-mobile-menu]');
      this.scroller = root.querySelector('[data-bdk-mobile-menu-scroll]');
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.isOpen = false;
      this.lastFocused = null;
      this.scrollY = 0;
      this.resizeFrame = null;
      this.bodyState = null;
      this.rewardsLauncherState = null;
      this.rewardsHideTimers = [];
      this.mobileBannersHydrated = false;

      if (!this.header || !this.toggle || !this.menu) return;

      this.updateViewport();
      this.bindEvents();
    }

    bindEvents() {
      this.toggle.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          this.isOpen ? this.close(true) : this.open();
        },
        { signal: this.signal }
      );

      this.root.addEventListener(
        'click',
        (event) => {
          if (!this.isOpen) return;

          const interactiveOverlay = event.target.closest(
            'header-search-popdown summary, [data-cart-toggle], .bdk-header-2026__mobile-wishlist'
          );
          if (interactiveOverlay) {
            this.close(false);
            return;
          }

          const menuLink = event.target.closest('[data-bdk-mobile-menu] a[href]');

          if (menuLink) {
            const href = menuLink.getAttribute('href') || '';
            const target = menuLink.getAttribute('target') || '';

            // Keep the menu visible during normal page navigation so the
            // current page is not exposed while the next one is loading.
            // Close it only when the current page remains visible.
            if (
              target === '_blank' ||
              href.startsWith('#') ||
              href.startsWith('javascript:') ||
              href.startsWith('mailto:') ||
              href.startsWith('tel:')
            ) {
              this.close(false);
            }
          }
        },
        { signal: this.signal }
      );

      document.addEventListener(
        'keydown',
        (event) => this.onKeydown(event),
        { signal: this.signal }
      );

      document.addEventListener(
        'theme:cart-drawer:open',
        () => this.close(false),
        { signal: this.signal }
      );

      this.root.addEventListener(
        'bdk:desktop-menu:open',
        () => this.close(false),
        { signal: this.signal }
      );

      window.addEventListener(
        'resize',
        () => this.scheduleResponsiveCheck(),
        { signal: this.signal, passive: true }
      );

      window.addEventListener(
        'pagehide',
        () => this.close(false),
        { signal: this.signal }
      );

      document.addEventListener(
        'theme:resize:width',
        () => this.scheduleResponsiveCheck(),
        { signal: this.signal }
      );
    }

    updateViewport() {
      if (!this.menu || !this.header) return;

      const headerBottom = this.header.getBoundingClientRect().bottom;
      const viewportTop = Math.max(0, Math.min(window.innerHeight, Math.round(headerBottom)));
      this.menu.style.setProperty('--bdk-mobile-menu-top', `${viewportTop}px`);
    }

    isVisible() {
      return Boolean(this.header && this.header.offsetParent !== null);
    }

    getFocusableElements() {
      const selector = [
        'a[href]',
        'button:not([disabled])',
        'summary',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',');

      return Array.from(this.root.querySelectorAll(selector)).filter((element) => {
        if (!(element instanceof HTMLElement)) return false;
        if (element.getAttribute('aria-hidden') === 'true') return false;
        if (element.closest('[aria-hidden="true"]')) return false;
        return element.offsetParent !== null;
      });
    }

    onKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = this.getFocusableElements();
      if (!focusable.length) {
        event.preventDefault();
        this.toggle?.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !this.root.contains(active))) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    lockScroll() {
      if (this.bodyState) return;

      const body = document.body;
      const html = document.documentElement;
      this.scrollY = window.scrollY;
      this.bodyState = {
        position: body.style.position,
        top: body.style.top,
        width: body.style.width,
        overflow: body.style.overflow,
        htmlOverflow: html.style.overflow,
      };

      body.style.position = 'fixed';
      body.style.top = `-${this.scrollY}px`;
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      html.style.overflow = 'hidden';
      html.classList.add('bdk-mobile-menu-open');
    }

    unlockScroll() {
      if (!this.bodyState) return;

      const body = document.body;
      const html = document.documentElement;
      const state = this.bodyState;

      body.style.position = state.position;
      body.style.top = state.top;
      body.style.width = state.width;
      body.style.overflow = state.overflow;
      html.style.overflow = state.htmlOverflow;
      html.classList.remove('bdk-mobile-menu-open');
      this.bodyState = null;
      window.scrollTo(0, this.scrollY);
    }

    findInOpenRoots(root, selector) {
      if (!root?.querySelector) return null;

      const directMatch = root.querySelector(selector);
      if (directMatch) return directMatch;

      const elements = root.querySelectorAll('*');
      for (const element of elements) {
        if (!element.shadowRoot) continue;
        const nestedMatch = this.findInOpenRoots(element.shadowRoot, selector);
        if (nestedMatch) return nestedMatch;
      }

      return null;
    }

    hideRewardsLauncher() {
      const cachedLauncher = this.rewardsLauncherState?.element;
      const launcher = cachedLauncher?.isConnected
        ? cachedLauncher
        : this.findInOpenRoots(document, '.gw-rd-popup-widget-launcher-btn');
      if (!(launcher instanceof HTMLElement)) return;

      if (!this.rewardsLauncherState || this.rewardsLauncherState.element !== launcher) {
        this.rewardsLauncherState = {
          element: launcher,
          visibility: launcher.style.getPropertyValue('visibility'),
          visibilityPriority: launcher.style.getPropertyPriority('visibility'),
          opacity: launcher.style.getPropertyValue('opacity'),
          opacityPriority: launcher.style.getPropertyPriority('opacity'),
          pointerEvents: launcher.style.getPropertyValue('pointer-events'),
          pointerEventsPriority: launcher.style.getPropertyPriority('pointer-events'),
        };
      }

      launcher.style.setProperty('visibility', 'hidden', 'important');
      launcher.style.setProperty('opacity', '0', 'important');
      launcher.style.setProperty('pointer-events', 'none', 'important');
      launcher.setAttribute('data-bdk-hidden-by-mobile-menu', 'true');
    }

    clearRewardsHideTimers() {
      this.rewardsHideTimers.forEach((timer) => window.clearTimeout(timer));
      this.rewardsHideTimers = [];
    }

    scheduleRewardsLauncherHide() {
      this.clearRewardsHideTimers();
      [0, 200, 650, 1500].forEach((delay) => {
        const timer = window.setTimeout(() => {
          if (this.isOpen) this.hideRewardsLauncher();
        }, delay);
        this.rewardsHideTimers.push(timer);
      });
    }

    restoreRewardsLauncher() {
      const state = this.rewardsLauncherState;
      if (!state?.element) return;

      const restore = (property, value, priority) => {
        if (value) state.element.style.setProperty(property, value, priority || '');
        else state.element.style.removeProperty(property);
      };

      restore('visibility', state.visibility, state.visibilityPriority);
      restore('opacity', state.opacity, state.opacityPriority);
      restore('pointer-events', state.pointerEvents, state.pointerEventsPriority);
      state.element.removeAttribute('data-bdk-hidden-by-mobile-menu');
      this.rewardsLauncherState = null;
    }

    hydrateMobileBanners() {
      if (this.mobileBannersHydrated || !this.menu) return;

      const images = Array.from(
        this.menu.querySelectorAll('[data-bdk-mobile-banner-image]')
      );

      images.forEach((image, index) => {
        if (!(image instanceof HTMLImageElement)) return;

        const src = image.dataset.src || '';
        const srcset = image.dataset.srcset || '';
        const sizes = image.dataset.sizes || '';

        image.loading = index === 0 ? 'eager' : 'lazy';
        image.setAttribute('fetchpriority', index === 0 ? 'auto' : 'low');
        if (sizes) image.sizes = sizes;
        if (srcset) image.srcset = srcset;
        if (src) image.src = src;

        delete image.dataset.src;
        delete image.dataset.srcset;
        delete image.dataset.sizes;
      });

      this.mobileBannersHydrated = true;
    }

    open() {
      if (!this.menu || !this.toggle || this.isOpen || !this.isVisible()) return;

      this.hydrateMobileBanners();
      this.root.dispatchEvent(new CustomEvent('bdk:mobile-menu:open'));
      this.lastFocused = document.activeElement;
      this.updateViewport();
      this.isOpen = true;
      this.root.classList.add('is-mobile-menu-open', 'meganav--visible');
      this.menu.setAttribute('aria-hidden', 'false');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.toggle.setAttribute(
        'aria-label',
        this.toggle.dataset.closeLabel || 'Close menu'
      );
      if (this.scroller) this.scroller.scrollTop = 0;
      this.lockScroll();
      this.scheduleRewardsLauncherHide();

      requestAnimationFrame(() => {
        this.updateViewport();
        this.toggle?.focus({ preventScroll: true });
      });
    }

    close(returnFocus = false) {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.root.classList.remove('is-mobile-menu-open', 'meganav--visible');
      this.menu?.setAttribute('aria-hidden', 'true');
      this.toggle?.setAttribute('aria-expanded', 'false');
      this.toggle?.setAttribute(
        'aria-label',
        this.toggle?.dataset.openLabel || 'Open menu'
      );
      this.clearRewardsHideTimers();
      this.restoreRewardsLauncher();
      this.unlockScroll();

      if (returnFocus && this.lastFocused instanceof HTMLElement) {
        this.lastFocused.focus({ preventScroll: true });
      }
    }

    scheduleResponsiveCheck() {
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.updateViewport();
        if (this.isOpen && !this.isVisible()) this.close(false);
      });
    }

    destroy() {
      this.close(false);
      this.clearRewardsHideTimers();
      this.restoreRewardsLauncher();
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.abortController.abort();
    }
  }

  class BadinkaHeader2026 {
    constructor(root) {
      this.root = root;
      this.desktop = root.querySelector('[data-bdk-desktop-header]');
      this.toggle = root.querySelector('[data-bdk-menu-toggle]');
      this.menu = root.querySelector('[data-bdk-mega-menu]');
      this.tabs = Array.from(root.querySelectorAll('[data-bdk-menu-tab]'));
      this.panels = Array.from(root.querySelectorAll('[data-bdk-menu-panel]'));
      this.rewardsButton = root.querySelector('[data-bdk-rewards-trigger]');
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.isOpen = false;
      this.lastFocused = null;
      this.resizeFrame = null;
      this.rewardsHideTimers = [];
      this.rewardsLauncherState = null;
      this.config = this.parseJson(CONFIG_SELECTOR, {});
      this.previewEntries = this.parseJson(PREVIEWS_SELECTOR, []);
      this.previewMap = this.buildPreviewMap(this.previewEntries);
      this.panelImageStates = new WeakMap();
      this.prefersReducedMotion = window.matchMedia(REDUCED_MOTION_QUERY).matches;
      this.mobileController = new BadinkaMobileMenu2026(root);

      Object.defineProperty(root, 'badinkaHeaderConfig', {
        value: this.config,
        configurable: true,
        writable: false,
      });

      this.reorderUtilities();
      this.updateMenuViewport();
      this.setupPanelMedia();
      this.bindEvents();
      this.activateInitialPanel();
      root.dataset.bdkHeaderInitialized = 'true';
    }

    parseJson(selector, fallback) {
      const node = this.root.querySelector(selector);
      if (!node) return fallback;

      try {
        return JSON.parse(node.textContent || '') || fallback;
      } catch (error) {
        console.warn(`BADINKA Header 2026: invalid JSON in ${selector}.`, error);
        return fallback;
      }
    }

    buildPreviewMap(entries) {
      const map = new Map();
      if (!Array.isArray(entries)) return map;

      entries.forEach((entry) => {
        const matchPath = this.normalizePath(entry?.matchPath);
        const panelKey = this.normalizePanelKey(entry?.panelKey) || '*';
        const matchLabel = this.normalizePreviewLabel(entry?.matchLabel);
        const src = typeof entry?.src === 'string' ? entry.src.trim() : '';
        const srcset = typeof entry?.srcset === 'string' ? entry.srcset.trim() : '';

        if (!matchPath || !src) return;

        map.set(`${panelKey}|${matchPath}|${matchLabel}`, {
          src,
          srcset,
          alt: typeof entry?.alt === 'string' ? entry.alt : '',
        });
      });

      return map;
    }

    normalizePreviewLabel(value) {
      return String(value || '')
        .normalize('NFKC')
        .trim()
        .toLocaleLowerCase()
        .replace(/\s+/g, ' ');
    }

    normalizePanelKey(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    normalizePath(value) {
      const raw = String(value || '').trim();
      if (!raw || raw.startsWith('#')) return '';

      try {
        const url = new URL(raw, window.location.origin);
        let path = url.pathname || '/';

        path = path.replace(/^\/(?:de-de|nl-nl|en-en|de|nl|en)(?=\/|$)/i, '');
        if (!path.startsWith('/')) path = `/${path}`;
        path = path.replace(/\/{2,}/g, '/');
        path = path.length > 1 ? path.replace(/\/+$/, '') : path;

        return path.toLowerCase();
      } catch (error) {
        return '';
      }
    }

    isPopupInteraction(target) {
      if (!(target instanceof Element)) return false;

      return Boolean(
        target.closest(
          'dialog, [role="dialog"], [aria-modal="true"], [data-section-type="popups"], .shopify-section-popups'
        )
      );
    }

    reorderUtilities() {
      const utilities = this.root.querySelector(
        '.bdk-header-2026__utilities .header__desktop__buttons'
      );
      if (!utilities) return;

      const cartButton = utilities.querySelector('[data-cart-toggle]')?.closest(
        '.header__desktop__button'
      );
      const searchButton = utilities.querySelector('header-search-popdown')?.closest(
        '.header__desktop__button'
      );

      if (cartButton && searchButton && searchButton.previousElementSibling !== cartButton) {
        utilities.insertBefore(cartButton, searchButton);
      }
    }

    setupPanelMedia() {
      this.panels.forEach((panel) => {
        const media = panel.querySelector('[data-bdk-menu-media]');
        if (!media) return;

        const layers = Array.from(media.querySelectorAll('[data-bdk-menu-image-layer]'));
        if (!layers.length) return;

        layers.forEach((layer) => {
          layer.classList.remove('is-visible');
          layer.setAttribute('aria-hidden', 'true');
          layer.dataset.source = '';
        });

        this.panelImageStates.set(panel, {
          media,
          layers,
          current: null,
          currentSrc: '',
          pendingSrc: '',
          defaultSrc: media.dataset.defaultSrc || '',
          defaultSrcset: media.dataset.defaultSrcset || '',
          defaultAlt: media.dataset.defaultAlt || '',
          requestToken: 0,
        });
      });
    }

    bindEvents() {
      if (this.toggle && this.menu) {
        this.toggle.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            this.isOpen ? this.close(true) : this.open();
          },
          { signal: this.signal }
        );
      }

      if (this.rewardsButton) {
        this.rewardsButton.addEventListener(
          'click',
          (event) => this.openRewards(event),
          { signal: this.signal }
        );
      }

      this.tabs.forEach((tab) => {
        tab.addEventListener(
          'click',
          (event) => {
            if (tab.matches('a[href]')) return;

            event.preventDefault();
            this.activatePanel(tab.dataset.panelTarget);
          },
          { signal: this.signal }
        );

        tab.addEventListener(
          'pointerenter',
          () => this.activatePanel(tab.dataset.panelTarget),
          { signal: this.signal }
        );

        tab.addEventListener(
          'focus',
          () => this.activatePanel(tab.dataset.panelTarget),
          { signal: this.signal }
        );

        tab.addEventListener(
          'keydown',
          (event) => this.onTabKeydown(event, tab),
          { signal: this.signal }
        );
      });

      this.panels.forEach((panel) => this.bindPanelPreviewEvents(panel));

      this.root.addEventListener(
        'click',
        (event) => {
          const tracked = event.target.closest('[data-bdk-menu-track]');
          if (tracked && this.root.contains(tracked)) {
            this.trackClick(tracked);
          }

          if (
            this.isOpen &&
            event.target.closest(
              'header-search-popdown summary, [data-cart-toggle], .popout__toggle'
            )
          ) {
            this.close(false);
          }
        },
        { signal: this.signal }
      );

      this.root.addEventListener(
        'bdk:mobile-menu:open',
        () => this.close(false),
        { signal: this.signal }
      );

      document.addEventListener(
        'click',
        (event) => {
          const externalMenuTrigger = event.target.closest('[data-bdk-open-mobile-menu]');
          if (externalMenuTrigger && this.mobileController?.isVisible()) {
            event.preventDefault();
            this.mobileController.open();
            return;
          }

          const contextTrackedLink = event.target.closest(
            '[data-bdk-mobile-context-links] [data-bdk-menu-track]'
          );
          if (contextTrackedLink) this.trackClick(contextTrackedLink);
        },
        { signal: this.signal }
      );

      document.addEventListener(
        'pointerdown',
        (event) => {
          if (
            this.isOpen &&
            !this.root.contains(event.target) &&
            !this.isPopupInteraction(event.target)
          ) {
            this.close(false);
          }
        },
        { signal: this.signal }
      );

      document.addEventListener(
        'keydown',
        (event) => {
          if (!this.isOpen || event.key !== 'Escape') return;

          event.preventDefault();
          this.close(true);
        },
        { signal: this.signal }
      );

      document.addEventListener(
        'theme:cart-drawer:open',
        () => this.close(false),
        { signal: this.signal }
      );

      document.addEventListener(
        'theme:resize:width',
        () => this.scheduleResponsiveCheck(),
        { signal: this.signal }
      );

      window.addEventListener(
        'resize',
        () => this.scheduleResponsiveCheck(),
        { signal: this.signal, passive: true }
      );

      window.addEventListener(
        'scroll',
        () => {
          if (this.isOpen) this.scheduleResponsiveCheck();
        },
        { signal: this.signal, passive: true }
      );
    }

    bindPanelPreviewEvents(panel) {
      const groups = panel.querySelector('.bdk-mega-menu__groups');
      if (!groups) return;

      let hoverTimer = null;
      let hoverTarget = null;
      let resetTimer = null;

      const findPreviewTarget = (target) => {
        if (!(target instanceof Element)) return null;

        const previewTarget = target.closest('[data-bdk-preview-path]');
        return previewTarget && groups.contains(previewTarget) ? previewTarget : null;
      };

      const clearHoverIntent = () => {
        if (hoverTimer) window.clearTimeout(hoverTimer);
        hoverTimer = null;
        hoverTarget = null;
      };

      const clearPreviewReset = () => {
        if (resetTimer) window.clearTimeout(resetTimer);
        resetTimer = null;
      };

      const schedulePanelDefault = () => {
        clearPreviewReset();
        resetTimer = window.setTimeout(() => {
          resetTimer = null;

          if (!this.isOpen || panel.hidden) return;

          const hoveredPreview = groups.querySelector('[data-bdk-preview-path]:hover');
          if (hoveredPreview) return;

          this.showPanelDefault(panel);
        }, PREVIEW_RESET_DELAY_MS);
      };

      const showFromTarget = (target) => {
        const previewTarget = findPreviewTarget(target);
        if (!previewTarget) {
          this.showPanelDefault(panel);
          return;
        }

        this.showPreviewForTarget(panel, previewTarget);
      };

      const scheduleFromTarget = (target) => {
        const previewTarget = findPreviewTarget(target);
        clearHoverIntent();

        if (!previewTarget) {
          schedulePanelDefault();
          return;
        }

        clearPreviewReset();
        hoverTarget = previewTarget;
        hoverTimer = window.setTimeout(() => {
          const targetToShow = hoverTarget;
          hoverTimer = null;
          hoverTarget = null;

          if (
            !this.isOpen ||
            panel.hidden ||
            !targetToShow?.isConnected ||
            !targetToShow.matches(':hover')
          ) {
            return;
          }

          this.showPreviewForTarget(panel, targetToShow);
        }, PREVIEW_HOVER_INTENT_MS);
      };

      groups.addEventListener(
        'pointerover',
        (event) => {
          const nextTarget = findPreviewTarget(event.target);
          const previousTarget = findPreviewTarget(event.relatedTarget);

          if (nextTarget && nextTarget === previousTarget) return;
          scheduleFromTarget(nextTarget || event.target);
        },
        { signal: this.signal }
      );

      groups.addEventListener(
        'focusin',
        (event) => {
          clearHoverIntent();
          clearPreviewReset();
          showFromTarget(event.target);
        },
        { signal: this.signal }
      );

      groups.addEventListener(
        'pointerleave',
        () => {
          clearHoverIntent();
          schedulePanelDefault();
        },
        { signal: this.signal }
      );

      groups.addEventListener(
        'focusout',
        () => {
          clearHoverIntent();
          clearPreviewReset();
          requestAnimationFrame(() => {
            const active = document.activeElement;
            const activePreview = active instanceof Element
              ? active.closest('[data-bdk-preview-path]')
              : null;

            if (!activePreview || !groups.contains(activePreview)) {
              this.showPanelDefault(panel);
            }
          });
        },
        { signal: this.signal }
      );
    }

    activateInitialPanel() {
      const activeTab = this.tabs.find((tab) => tab.classList.contains('is-active')) || this.tabs[0];
      if (activeTab) {
        this.activatePanel(activeTab.dataset.panelTarget, false);
      }
    }

    activatePanel(panelId, focusTab = false) {
      if (!panelId) return;

      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.panelTarget === panelId;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-expanded', String(isActive));
        if (isActive && focusTab) tab.focus();
      });

      this.panels.forEach((panel) => {
        const isActive = panel.id === panelId;
        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);
        if (isActive && this.isOpen) this.showPanelDefault(panel);
      });
    }

    findPreview(panel, rawPath, rawLabel = '') {
      const matchPath = this.normalizePath(rawPath);
      if (!matchPath) return null;

      const panelKey = this.normalizePanelKey(panel.dataset.panelKey);
      const matchLabel = this.normalizePreviewLabel(rawLabel);

      return (
        (matchLabel && this.previewMap.get(`${panelKey}|${matchPath}|${matchLabel}`)) ||
        (matchLabel && this.previewMap.get(`*|${matchPath}|${matchLabel}`)) ||
        this.previewMap.get(`${panelKey}|${matchPath}|`) ||
        this.previewMap.get(`*|${matchPath}|`) ||
        null
      );
    }

    showPreviewForTarget(panel, target) {
      const preview = this.findPreview(
        panel,
        target.dataset.bdkPreviewPath,
        target.dataset.bdkPreviewLabel
      );
      if (!preview) {
        this.showPanelDefault(panel);
        return;
      }

      this.showPanelImage(panel, preview.src, preview.alt, true, preview.srcset);
    }

    showPanelDefault(panel) {
      const state = this.panelImageStates.get(panel);
      if (!state) return;

      if (!state.defaultSrc) {
        this.clearPanelImage(panel);
        return;
      }

      this.showPanelImage(
        panel,
        state.defaultSrc,
        state.defaultAlt,
        false,
        state.defaultSrcset
      );
    }

    showPanelImage(panel, src, alt = '', fallbackToDefault = false, srcset = '') {
      const state = this.panelImageStates.get(panel);
      if (!state || !src) return;

      if (state.currentSrc === src) {
        if (state.pendingSrc && state.pendingSrc !== src) {
          state.requestToken += 1;
          state.pendingSrc = '';
        }
        return;
      }

      if (state.pendingSrc === src) return;

      const requestToken = ++state.requestToken;
      state.pendingSrc = src;
      const loader = new Image();
      const mediaWidth = Math.max(
        1,
        Math.ceil(state.media.getBoundingClientRect().width || state.media.clientWidth || 1)
      );

      loader.decoding = 'async';
      loader.sizes = `${mediaWidth}px`;
      if (srcset) loader.srcset = srcset;

      loader.onload = () => {
        if (requestToken !== state.requestToken) return;
        state.pendingSrc = '';

        const nextLayer = state.layers.find((layer) => layer !== state.current) || state.layers[0];
        if (!nextLayer) return;

        const resolvedSrc = loader.currentSrc || src;
        nextLayer.removeAttribute('srcset');
        nextLayer.removeAttribute('sizes');
        nextLayer.src = resolvedSrc;
        nextLayer.alt = alt;
        nextLayer.dataset.source = resolvedSrc;
        nextLayer.setAttribute('aria-hidden', 'false');

        const reveal = () => {
          if (state.current && state.current !== nextLayer) {
            state.current.classList.remove('is-visible');
            state.current.setAttribute('aria-hidden', 'true');
          }

          nextLayer.classList.add('is-visible');
          state.current = nextLayer;
          state.currentSrc = src;
          state.media.classList.remove('is-empty');
        };

        if (this.prefersReducedMotion) {
          reveal();
        } else {
          requestAnimationFrame(reveal);
        }
      };

      loader.onerror = () => {
        if (requestToken !== state.requestToken) return;
        state.pendingSrc = '';

        if (fallbackToDefault && state.defaultSrc && src !== state.defaultSrc) {
          this.showPanelDefault(panel);
        } else if (!state.current) {
          this.clearPanelImage(panel);
        }
      };

      loader.src = src;
    }

    clearPanelImage(panel) {
      const state = this.panelImageStates.get(panel);
      if (!state) return;

      state.requestToken += 1;
      state.layers.forEach((layer) => {
        layer.classList.remove('is-visible');
        layer.setAttribute('aria-hidden', 'true');
      });
      state.current = null;
      state.currentSrc = '';
      state.pendingSrc = '';
      state.media.classList.add('is-empty');
    }

    updateMenuViewport() {
      if (!this.menu || !this.desktop) return;

      const desktopBottom = this.desktop.getBoundingClientRect().bottom;
      const viewportTop = Math.max(0, Math.min(window.innerHeight, Math.round(desktopBottom)));
      this.menu.style.setProperty('--bdk-mega-menu-top', `${viewportTop}px`);
    }

    findInOpenRoots(root, selector) {
      if (!root?.querySelector) return null;

      const directMatch = root.querySelector(selector);
      if (directMatch) return directMatch;

      const elements = root.querySelectorAll('*');
      for (const element of elements) {
        if (!element.shadowRoot) continue;
        const nestedMatch = this.findInOpenRoots(element.shadowRoot, selector);
        if (nestedMatch) return nestedMatch;
      }

      return null;
    }

    findRewardsLauncher() {
      return this.findInOpenRoots(document, '.gw-rd-popup-widget-launcher-btn');
    }

    hideRewardsLauncher() {
      const cachedLauncher = this.rewardsLauncherState?.element;
      const launcher = cachedLauncher?.isConnected
        ? cachedLauncher
        : this.findRewardsLauncher();
      if (!(launcher instanceof HTMLElement)) return false;

      if (!this.rewardsLauncherState || this.rewardsLauncherState.element !== launcher) {
        const properties = ['visibility', 'opacity', 'pointer-events'];
        const values = {};

        properties.forEach((property) => {
          values[property] = {
            value: launcher.style.getPropertyValue(property),
            priority: launcher.style.getPropertyPriority(property),
          };
        });

        this.rewardsLauncherState = { element: launcher, values };
      }

      launcher.style.setProperty('visibility', 'hidden', 'important');
      launcher.style.setProperty('opacity', '0', 'important');
      launcher.style.setProperty('pointer-events', 'none', 'important');
      launcher.setAttribute('data-bdk-hidden-by-menu', 'true');
      return true;
    }

    restoreRewardsLauncher() {
      const state = this.rewardsLauncherState;
      if (!state?.element) return;

      Object.entries(state.values).forEach(([property, stored]) => {
        if (stored.value) {
          state.element.style.setProperty(property, stored.value, stored.priority || '');
        } else {
          state.element.style.removeProperty(property);
        }
      });

      state.element.removeAttribute('data-bdk-hidden-by-menu');
      this.rewardsLauncherState = null;
    }

    clearRewardsHideTimers() {
      this.rewardsHideTimers.forEach((timer) => window.clearTimeout(timer));
      this.rewardsHideTimers = [];
    }

    scheduleRewardsLauncherHide() {
      this.clearRewardsHideTimers();

      [0, 200, 650, 1500].forEach((delay) => {
        const timer = window.setTimeout(() => {
          if (this.isOpen) this.hideRewardsLauncher();
        }, delay);
        this.rewardsHideTimers.push(timer);
      });
    }

    openRewards(event) {
      event.preventDefault();

      const fallbackUrl = this.rewardsButton?.dataset.rewardsFallbackUrl || '';
      let launcher = this.findRewardsLauncher();

      this.clearRewardsHideTimers();
      this.restoreRewardsLauncher();
      this.close(false);

      requestAnimationFrame(() => {
        launcher = launcher || this.findRewardsLauncher();

        if (launcher instanceof HTMLElement) {
          launcher.setAttribute('data-bdk-rewards-position', 'desktop_menu');
          launcher.click();
          window.setTimeout(() => {
            launcher?.removeAttribute('data-bdk-rewards-position');
          }, 0);
          return;
        }

        if (fallbackUrl && fallbackUrl !== '#') {
          window.location.assign(fallbackUrl);
          return;
        }

        console.warn('BADINKA Header 2026: Growave Rewards launcher was not found.');
      });
    }

    open() {
      if (!this.menu || !this.toggle || this.isOpen || this.toggle.disabled) return;

      this.root.dispatchEvent(new CustomEvent('bdk:desktop-menu:open'));
      this.lastFocused = document.activeElement;
      this.updateMenuViewport();
      this.isOpen = true;
      this.root.classList.add('is-menu-open', 'meganav--visible');
      this.menu.setAttribute('aria-hidden', 'false');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.toggle.setAttribute(
        'aria-label',
        this.toggle.dataset.closeLabel || 'Close'
      );

      const activePanel = this.panels.find((panel) => !panel.hidden);
      if (activePanel) this.showPanelDefault(activePanel);
      this.scheduleRewardsLauncherHide();

      requestAnimationFrame(() => {
        const activeTab = this.tabs.find((tab) => tab.classList.contains('is-active'));
        activeTab?.focus({ preventScroll: true });
      });
    }

    close(returnFocus = false) {
      if (!this.isOpen) return;

      const activePanel = this.panels.find((panel) => !panel.hidden);
      if (activePanel) this.showPanelDefault(activePanel);

      this.isOpen = false;
      this.clearRewardsHideTimers();
      this.restoreRewardsLauncher();
      this.root.classList.remove('is-menu-open', 'meganav--visible');
      this.menu?.setAttribute('aria-hidden', 'true');
      this.toggle?.setAttribute('aria-expanded', 'false');
      this.toggle?.setAttribute(
        'aria-label',
        this.toggle?.dataset.openLabel || 'Menu'
      );

      if (returnFocus && this.lastFocused instanceof HTMLElement) {
        this.lastFocused.focus({ preventScroll: true });
      }
    }

    onTabKeydown(event, currentTab) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      event.preventDefault();
      const currentIndex = this.tabs.indexOf(currentTab);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;

      const nextTab = this.tabs[nextIndex];
      if (nextTab) {
        this.activatePanel(nextTab.dataset.panelTarget, true);
      }
    }

    scheduleResponsiveCheck() {
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.updateMenuViewport();
        if (this.isOpen && this.desktop && this.desktop.offsetParent === null) {
          this.close(false);
        }
      });
    }

    trackClick(element) {
  if (!(element instanceof HTMLElement) || element.getAttribute('aria-disabled') === 'true') {
    return;
  }

  const menuText = element.dataset.menuText || element.textContent.trim();
  const menuUrl = element.dataset.menuUrl || element.getAttribute('href') || '';

  const rawContext = element.dataset.menuContext || 'desktop';
  const rawLevel = element.dataset.menuLevel || 'unknown';
  const rawSection = element.dataset.menuSection || 'unknown';
  const rawType = element.dataset.menuType || 'text_link';

  const isMobile = rawContext.startsWith('mobile');
  const menuContext = isMobile ? 'mobile' : 'desktop';

  let menuLevel = 'unknown';
  let menuSection = rawSection;

  if (menuContext === 'desktop') {
    if (rawLevel === 'toplevel' || rawLevel === 'child' || rawLevel === 'grandchild') {
      menuLevel = rawLevel;
    } else if (rawLevel === 'utility') {
      menuLevel = 'toplevel';
      menuSection = menuText;
    } else if (rawLevel === 'footer') {
      menuLevel = rawType === 'qr_link' || rawType === 'store_badge'
        ? 'image'
        : 'child';
    } else if (rawLevel === 'image') {
      menuLevel = 'image';
    }
  } else {
    const isFeatured = rawLevel === 'featured'
      || rawContext === 'mobile_banner'
      || rawSection === 'MOBILE FEATURED';

    menuLevel = isFeatured ? 'toplevel' : 'submenu';

    if (rawLevel !== '2') {
      menuSection = 'unknown';
    }
  }

  let menuType = 'text_link';

  if (rawType === 'open_submenu') {
    menuType = 'open_submenu';
  } else if (
    rawType === 'image_link'
    || rawType === 'qr_link'
    || rawType === 'store_badge'
  ) {
    menuType = 'image_link';
  }

  const locale = String(this.config.locale || 'en').toLowerCase();

  let menuLanguage = 'en';

  if (locale.startsWith('de')) {
    menuLanguage = 'de';
  } else if (locale.startsWith('nl')) {
    menuLanguage = 'nl';
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'menu_click',
    menu_text: menuText,
    menu_level: menuLevel,
    menu_url: menuUrl,
    menu_section: menuSection,
    menu_type: menuType,
    menu_context: menuContext,
    menu_language: menuLanguage,
  });
}

    destroy() {
      this.close(false);
      this.mobileController?.destroy();
      this.clearRewardsHideTimers();
      this.restoreRewardsLauncher();
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
      this.abortController.abort();
      this.root.removeAttribute('data-bdk-header-initialized');
      instances.delete(this.root);
    }
  }

  const initRoot = (root) => {
    if (!(root instanceof HTMLElement) || instances.has(root)) return;
    instances.set(root, new BadinkaHeader2026(root));
  };

  const initAll = (scope = document) => {
    if (scope instanceof HTMLElement && scope.matches(ROOT_SELECTOR)) {
      initRoot(scope);
    }

    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => {
      instances.get(root)?.destroy();
    });
  });
})();
